import { Injectable } from '@angular/core';
import { Observable, shareReplay, map } from 'rxjs';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { BidTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/bidType.service';
import {ServerTranslateService} from "@services/server-translate.service";

@Injectable({
  providedIn: 'root'
})
export class MetadataCacheService {
  private regionsCache$?: Observable<any>;
  private cropsCache$?: Observable<any>;
  private bidTypesCache$?: Observable<any>;

  constructor(
    private regionService: RegionService,
    private cropService: CropService,
    private bidTypeService: BidTypeService,
    public serverTranslateService:ServerTranslateService
  ) {}

  getRegions(): Observable<any> {
    if (!this.regionsCache$) {
      const translate = this.serverTranslateService
      this.regionsCache$ = this.regionService.dictionariesRegionGetByQueryPost({
        take: 1000,
        offset: 0
      }).pipe(
        map((regions: any[]) => ({
          items: regions.map((region: any) => ({
            id: region.id,
            nameRu: region.nameRu,
            nameEn: region.nameEn,
            nameUz: region.nameUz,
            get name():string  {
              return translate.translate(region, 'name') as string;
            }
          }))
        })),
        shareReplay(1) 
      );
    }
    return this.regionsCache$;
  }

  getCrops(): Observable<any> {
      const translate = this.serverTranslateService

      if (!this.cropsCache$) {
      this.cropsCache$ = this.cropService.dictionariesCropGetByQueryPost({
        take: 1000,
        offset: 0
      }).pipe(
        map((crops: any[]) => {
          const mappedCrops = crops.map((crop: any) => ({
            id: crop.id,
            nameRu: crop.nameRu,
            nameEn: crop.nameEn,
            nameUz: crop.nameUz,
            get name():string  {
              return translate.translate(crop, 'name') as string;
            }
          }));

          const sortedCrops = this.sortCropsAlphabetically(mappedCrops, translate);

          return {
            items: sortedCrops
          };
        }),
        shareReplay(1) 
      );
    }
    return this.cropsCache$;
  }

  getBidTypes(): Observable<any> {
    const translate = this.serverTranslateService

    if (!this.bidTypesCache$) {
      this.bidTypesCache$ = this.bidTypeService.dictionariesBidTypeGetByQueryPost({
        take: 1000,
        offset: 0
      }).pipe(
        map((bidTypes: any[]) => ({
          items: bidTypes.map((bidType: any) => ({
            id: bidType.id,
            nameRu: bidType.nameRu,
            nameEn: bidType.nameEn,
            nameUz: bidType.nameUz,
            get name():string  {
              return translate.translate(bidType, 'name') as string;
            },
          }))
        })),
        shareReplay(1) 
      );
    }
    return this.bidTypesCache$;
  }

  clearCache(): void {
    this.regionsCache$ = undefined;
    this.cropsCache$ = undefined;
    this.bidTypesCache$ = undefined;
  }

  private sortCropsAlphabetically(crops: any[], translate: any): any[] {
    if (!crops || crops.length === 0) {
      return crops;
    }

    const currentLang = translate.currentLang || 'ru';

    let locale = 'ru-RU'; 

    if (currentLang === 'en' || currentLang === 'en-us' || currentLang === 'en-US') {
      locale = 'en-US';
    } else if (currentLang === 'uz' || currentLang === 'uz-latn' || currentLang === 'uz-Latn-UZ') {
      locale = 'uz-Latn-UZ';
    } else if (currentLang === 'ru' || currentLang === 'ru-ru' || currentLang === 'ru-RU') {
      locale = 'ru-RU';
    }

    return crops.sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';

      return nameA.localeCompare(nameB, locale, {
        sensitivity: 'base', 
        numeric: true,      
        ignorePunctuation: true 
      });
    });
  }
}